import { FormGroup, FormParagraph, FormSection } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPatient } from '@common/interfaces/patient';
import type { RespiAllerReferralData } from '@common/validation/customControls/allergology/respiAllerReferral/v1/respiAllerReferralSchemaV1';
import type { IReduxState } from '@frontend/configureStore';
import { CustomControlMode, ServiceCode } from '@common/enums';
import { CareProviderSelector } from '@frontend/components/pages/customControls/utils';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useProcessActivityEditContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { ContraIndicationsAlert } from '../../ContraIndicationsAlert';
import { EmailAddressFormGroup } from '../../EmailAddressFormGroup';
import { PatientInsurance } from '../../patientInsurance/PatientInsurance';
import { PhoneNumberFormGroup } from '../../PhoneNumberFormGroup';
import { ProcessActivityAnamnesisAddEdit } from '../../processActivityAnamnesis/ProcessActivityAnamnesisAddEdit';
import { TextInputFormGroup } from '../../processActivityFormGroupFields';
import { ProcessActivityMedicationsAddEdit } from '../../processActivityMedications/ProcessActivityMedicationsAddEdit';
import { ProcessActivityProfessionalSummaryDataAddEdit } from '../../processActivityProfessionalSummaryData/ProcessActivityProfessionalSummaryDataAddEdit';
import { RespiAllerFollowingSteps } from './RespiAllerFollowingSteps';

export function RespiAllerReferralEdit() {
    const translate = getSelectedLanguage();
    const abortSignal = useAbortSignal();
    const { processActivityId, errors, data, handleCancel, onChange } =
        useProcessActivityEditContext<RespiAllerReferralData>();
    const { patient } = usePatientEpisodeCategoryContext();
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const [isHighPrivacy, setIsHighPrivacy] = useState(false);

    useEffect(() => {
        getHighPrivacySetting();
    }, []);

    useEffect(() => {
        if (!data.patient.emailAddress) {
            onChange(
                {
                    patient: {
                        ...getInitialPatientData(data, patient),
                    },
                },
                ['patient']
            );
        }

        if (!data.draftCareProviderOrganizationId && !data.draftCareProviderPractitionerId) {
            onChange({
                draftCareProviderOrganizationId: patient.careProviderOrganizationId,
                draftCareProviderPractitionerId: patient.careProviderPractitionerId,
            });
        }
    }, [patient]);

    return (
        <>
            <PatientInsurance
                isPatientInformed={data.isPatientInformed}
                mode={CustomControlMode.edit}
                onCancel={handleCancel}
                onChange={(params) => onChange(params, 'isPatientInformed')}
                serviceCode={ServiceCode.RespiAller}
            />

            <ContraIndicationsAlert
                absoluteContraIndications={translate.allergology.contraIndications.absoluteContraIndications}
                absoluteContraIndicationsTitle={translate.allergology.contraIndications.absoluteContraIndicationsTitle}
                disclaimer={translate.allergology.contraIndications.disclaimer}
                relativeContradictions={translate.allergology.contraIndications.relativeContradictions}
                relativeContradictionsTitle={translate.allergology.contraIndications.relativeContradictionsTitle}
                subtitle={translate.allergology.contraIndications.subtitle}
                title={translate.allergology.contraIndications.title}
            />

            <CareProviderSelector
                contextPatient={patient}
                entity={{
                    draftCareProviderOrganizationId:
                        data.draftCareProviderOrganizationId ?? patient.careProviderOrganizationId ?? null,
                    draftCareProviderPractitionerId:
                        data.draftCareProviderPractitionerId ?? patient.careProviderPractitionerId ?? null,
                }}
                errors={errors}
                handleChange={(name, value) => onChange({ [name]: value }, name)}
                isHighPrivacy={isHighPrivacy}
                validateOne={() => {}}
            />

            <FormSection
                isBorderless={true}
                title={translate.common.referral}
            >
                <FormGroup label={translate.common.sendTo}>
                    <FormParagraph>{translate.companies.ksyosAllergology}</FormParagraph>
                </FormGroup>
            </FormSection>

            <ProcessActivityAnamnesisAddEdit
                anamnesis={data.anamnesis}
                errors={errors}
                onChange={onChange}
            />

            <ProcessActivityMedicationsAddEdit
                errors={errors}
                onChange={onChange}
                patientUsesMedication={data.patientUsesMedication}
                processActivityId={processActivityId}
            />

            <ProcessActivityProfessionalSummaryDataAddEdit
                onChange={onChange}
                processActivityId={processActivityId}
            />

            <FormSection title={translate.common.referralQuestion}>
                <TextInputFormGroup
                    fieldKey="referralQuestion"
                    label={translate.allergology.allergistReferralQuestion}
                />
            </FormSection>

            <FormSection title={translate.common.contactPatient}>
                <EmailAddressFormGroup
                    data={data}
                    errors={errors}
                    onChange={onChange}
                />
                <PhoneNumberFormGroup
                    data={{
                        homePhoneNumber: data.patient.homePhoneNumber,
                        mobileNumber: data.patient.mobileNumber,
                    }}
                    errors={errors}
                    onChange={(value) => onChange({ patient: { ...data.patient, ...value } }, ['patient'])}
                />
            </FormSection>

            <RespiAllerFollowingSteps />
        </>
    );

    function getInitialPatientData(processData: RespiAllerReferralData, contextPatient: IPatient) {
        const email = processData.patient.emailAddress ?? contextPatient.emailAddress;
        const homePhoneNumber = processData.patient.homePhoneNumber ?? contextPatient.homePhoneNumber;
        const mobileNumber = processData.patient.mobileNumber ?? contextPatient.mobileNumber;

        return {
            emailAddress: email ?? null,
            homePhoneNumber: homePhoneNumber ?? null,
            mobileNumber: mobileNumber ?? null,
        };
    }

    async function getHighPrivacySetting() {
        const organization = await getOneOrganization(organizationId, abortSignal);
        setIsHighPrivacy(Boolean(organization?.isHighPrivacy));
    }
}
